
import { useEffect, useState, Dispatch, SetStateAction } from 'react';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle
} from '@mui/material';
import { useUser } from '@hooks/useUser';
import useSearchOfficials, { SearchResultOption } from '@hooks/useSearchOfficials';
import { PrimaryButton, SearchAndSelect, SecondaryButton } from '@components';
import { OperationIncident, updateOperationIncident } from '@networkRequest/operationsService';
import Utils from '../../../../utils/utils';

const EditPersonnelDialog = ({
  open,
  onClose,
  operationId,
  incident,
  setIncident,
  setError,
}: {
  open: boolean;
  onClose: () => void;
  operationId: string;
  incident: OperationIncident;
  setIncident: Dispatch<SetStateAction<OperationIncident | null>>;
  setError: Dispatch<SetStateAction<string | null>>;
}) => {
  const { user } = useUser();
  const { searchResultOptions, handleSearch, parseOfficials } = useSearchOfficials({ operationId });

  if (!user) return null;

  const [activityOfficials, setActivityOfficials] = useState<{ id: string, label: string }[]>([]);

  useEffect(() => {
    if (!incident.officials?.length) return;

    setActivityOfficials(incident.officials.map((official) => ({
      id: official.id,
      label: Utils.fullName(official.agency_official.first_name, official.agency_official.last_name),
      searchResult: {
        kind: 'operation_agency_official',
      },
    })));
  }, [incident]);

  const handleSelectSearchResult = (_e, newValue: SearchResultOption[], reason) => {
    if (reason === 'clear' || !newValue.length) {
      setActivityOfficials([]);
    }

    if (!newValue) {
      return;
    }

    const parsedResults = parseOfficials(newValue);
    setActivityOfficials(parsedResults);
  }

  const handleCancel = () => {
    setActivityOfficials([]);
    onClose();
  };

  const handleSubmit = async () => {
    const { data, error } = await updateOperationIncident({
      accessToken: user.accessToken,
      incidentId: incident.id,
      payload: {
        official_ids: activityOfficials.map((official) => official.id),
      },
    });

    if (data) {
      setIncident(data);
    } else {
      setError(error ?? 'Failed to update incident personnel');
    }
    onClose();
  };

  return (
    <Dialog
      open={open}
      slotProps={{
        backdrop: { invisible: true },
        paper: { style: { borderRadius: 8, width: '783px' } }
      }}
    >
      <DialogTitle sx={{ padding: 3 }} variant='h5'>
        Edit Incident Personnel
      </DialogTitle>
      <DialogContent sx={{ pt: '8px !important' }}>
        <SearchAndSelect
          label='Add Personnel'
          placeholder='Search for individuals or teams'
          options={searchResultOptions}
          onInputChange={handleSearch}
          value={activityOfficials}
          onChange={handleSelectSearchResult}
        />
      </DialogContent>

        <DialogActions
          sx={{
            paddingTop: 1,
            paddingBottom: 3,
            paddingRight: 3,
            paddingLeft: 3,
          }}
          >
          <SecondaryButton action={handleCancel}>
            Cancel
          </SecondaryButton>

          <PrimaryButton type='submit' action={handleSubmit}>Submit</PrimaryButton>
        </DialogActions>
    </Dialog>
  );
};

export default EditPersonnelDialog; 